import { observer } from 'mobx-react-lite'
import { useState, useEffect } from 'react'

import { useAppStore } from '@src/app/context'
import AudioSettingsModal from '@src/app/voice/AudioSettingsModal'
import CallProvider from '@src/app/voice/ongoing-call/context/CallProvider'
import useCallError from '@src/app/voice/useCallError'
import useBeforeUnload from '@src/lib/hooks/useBeforeUnload'
import useReaction from '@src/lib/hooks/useReaction'
import type { FeaturesPreviewTrigger } from '@src/service/analytics/WorkspaceAnalyticsStore'
import type { ActiveCall } from '@src/service/voice'
import { LoadingIndicator } from '@ui/LoadingIndicator'

import CallHeader from './CallHeader'
import Keypad from './Keypad'
import * as styles from './OngoingCall.css'
import SelectionHeader from './SelectionHeader'
import { AddParticipant } from './add-participant'
import CallActions from './call-actions/CallActions'
import { ParticipantsList } from './participants-list'
import { CallTransfer } from './transfer'
import type { OngoingCallMode } from './types'

interface OngoingCallProps {
  call: ActiveCall
  onModeChange: () => void
}

const OngoingCall = ({ call, onModeChange }: OngoingCallProps) => {
  const { command } = useAppStore()
  const [mode, setMode] = useState<OngoingCallMode | null>(null)
  const loading = !call.roomReady

  useBeforeUnload({ showNativePrompt: true })

  useEffect(() => onModeChange(), [mode, onModeChange])

  useCallError(call)

  useReaction(
    () => call.isGroupCall,
    () => {
      if (mode === 'transfer' && call.isGroupCall) {
        setMode(null)
      }
    },
    {},
    [call],
  )

  const handleDTMF = (digit: string) => {
    call.sendDigits(digit)
  }

  const openAddParticipantWindow = () => {
    if (mode === 'add-participant') {
      handleClose()
    } else {
      setMode('add-participant')
    }
  }

  const openUpgradeModal = (trigger: FeaturesPreviewTrigger) => {
    command.present({ name: 'upgrade plan', trigger })
  }

  const handleClose = () => {
    setMode(null)
  }

  return (
    <CallProvider call={call}>
      <div role="dialog" aria-label="Call in progress" className={styles.root}>
        <div className={styles.leftPanel}>
          {loading ? null : (
            <CallHeader
              mode={mode}
              timerStartTimestamp={call.startedAt?.getTime()}
              addParticipantsCallback={openAddParticipantWindow}
              openUpgradeModal={openUpgradeModal}
            />
          )}
          <div className={styles.participants}>
            {loading ? (
              <div
                className={styles.spinnerWrapper}
                style={{
                  marginTop: 95,
                  marginBottom: 15,
                }}
              >
                <LoadingIndicator size={14} color="gray" variant="circular" />
              </div>
            ) : (
              <ParticipantsList />
            )}
            <CallActions
              mode={mode}
              loading={loading}
              setMode={setMode}
              closeActionPanel={handleClose}
              openUpgradeModal={openUpgradeModal}
            />
          </div>
        </div>
        {mode && (
          <div className={styles.rightPanel}>
            {mode === 'transfer' ? (
              <CallTransfer onClose={handleClose} />
            ) : mode === 'add-participant' ? (
              <AddParticipant onClose={handleClose} />
            ) : mode === 'keypad' ? (
              <Keypad onClose={handleClose} onKeyPressed={handleDTMF} />
            ) : mode === 'settings' ? (
              <>
                <SelectionHeader
                  title="Audio settings"
                  description="Manage your call audio settings"
                  handleBack={handleClose}
                />
                <AudioSettingsModal
                  allowTesting={false}
                  tooltipMode="dark"
                  className={styles.settings}
                />
              </>
            ) : null}
          </div>
        )}
      </div>
    </CallProvider>
  )
}

export default observer(OngoingCall)
